/**
 * ImageUpload 图片上传组件
 *
 * @description 支持拖拽上传、预签名直传 S3、文件去重的图片上传组件
 *
 * @example
 * ```tsx
 * // 基础用法
 * <ImageUpload
 *   value={imageUrl}
 *   onChange={(url) => setImageUrl(url)}
 * />
 *
 * // 自定义配置
 * <ImageUpload
 *   value={imageUrl}
 *   onChange={(url) => setImageUrl(url)}
 *   maxSize={5 * 1024 * 1024}
 *   accept="image/jpeg,image/png"
 *   placeholder="上传头像"
 * />
 * ```
 */
import { DeleteOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { message, Upload } from 'antd';
import type { RcFile, UploadProps } from 'antd/es/upload';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';

import { uploadFileSignatureApiAdminUploadSignaturePost } from '@/services/admin/fileStorage';

import {
  calculateFileHash,
  DEFAULT_MAX_SIZE,
  formatFileSize,
  getFileMimeType,
  IMAGE_ACCEPT,
  isImageFile,
} from './utils';

// ==================== 类型定义 ====================

export interface ImageUploadProps {
  /** 图片 URL */
  value?: string;
  /** 值变化回调 */
  onChange?: (url: string | undefined) => void;
  /** 最大文件大小（字节），默认 10MB */
  maxSize?: number;
  /** 接受的文件类型，默认常见图片格式 */
  accept?: string;
  /** 占位文字 */
  placeholder?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 图片宽度 */
  width?: number | string;
  /** 图片高度 */
  height?: number | string;
}

// ==================== 样式定义 ====================

const useStyles = createStyles(({ token, css }) => ({
  uploadContainer: css`
    .ant-upload-wrapper {
      display: block;
    }
  `,
  uploadButton: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: ${token.colorTextSecondary};
    cursor: pointer;
    background-color: ${token.colorFillAlter};
    border: 1px dashed ${token.colorBorder};
    border-radius: ${token.borderRadius}px;
    transition: all ${token.motionDurationMid};

    &:hover {
      border-color: ${token.colorPrimary};
      background-color: ${token.colorFillSecondary};
    }
  `,
  uploadIcon: css`
    font-size: 24px;
    color: ${token.colorTextSecondary};
  `,
  uploadText: css`
    margin-top: ${token.marginXS}px;
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextSecondary};
  `,
  imagePreview: css`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: ${token.borderRadius}px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  imageOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity ${token.motionDurationMid};

    &:hover {
      opacity: 1;
    }
  `,
  deleteButton: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 16px;
    color: #fff;
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transition: all ${token.motionDurationMid};

    &:hover {
      background-color: ${token.colorError};
    }
  `,
}));

// ==================== 组件实现 ====================

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  maxSize = DEFAULT_MAX_SIZE,
  accept = IMAGE_ACCEPT,
  placeholder = '上传图片',
  disabled = false,
  className,
  style,
  width = 104,
  height = 104,
}) => {
  const { styles, cx } = useStyles();
  const [loading, setLoading] = useState(false);

  // 上传前校验
  const beforeUpload = (file: RcFile): boolean | Promise<boolean> => {
    // 验证文件类型
    if (!isImageFile(file)) {
      message.error('请上传图片文件');
      return false;
    }

    // 验证文件大小
    if (file.size > maxSize) {
      message.error(`图片大小不能超过 ${formatFileSize(maxSize)}`);
      return false;
    }

    return true;
  };

  // 自定义上传逻辑
  const customUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    const uploadFile = file as RcFile;

    setLoading(true);

    try {
      // 1. 计算文件哈希
      const fileHash = await calculateFileHash(uploadFile);

      // 2. 获取上传签名
      const response = await uploadFileSignatureApiAdminUploadSignaturePost({
        file_hash: fileHash,
        filename: uploadFile.name,
        file_size: uploadFile.size,
        type: 'image',
        mime_type: getFileMimeType(uploadFile),
      });

      if (response.code !== 0 || !response.data) {
        throw new Error(response.message || '获取上传签名失败');
      }

      const { is_exists, file_url, upload_url } = response.data;

      // 3. 如果文件已存在，直接使用现有 URL
      if (is_exists && file_url) {
        onChange?.(file_url);
        onSuccess?.(response.data);
        message.success('图片上传成功（秒传）');
        return;
      }

      // 4. 文件不存在，上传到 S3
      if (!upload_url) {
        throw new Error('获取上传地址失败');
      }

      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'Content-Type': getFileMimeType(uploadFile),
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('文件上传失败');
      }

      // 5. 构建文件访问 URL（从预签名 URL 中提取）
      const fileAccessUrl = upload_url.split('?')[0];
      onChange?.(fileAccessUrl);
      onSuccess?.(response.data);
      message.success('图片上传成功');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '上传失败';
      message.error(errorMessage);
      onError?.(error as Error);
    } finally {
      setLoading(false);
    }
  };

  // 删除图片
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(undefined);
  };

  // 渲染上传按钮
  const renderUploadButton = () => (
    <div className={styles.uploadButton} style={{ width, height }}>
      {loading ? (
        <LoadingOutlined className={styles.uploadIcon} />
      ) : (
        <PlusOutlined className={styles.uploadIcon} />
      )}
      <span className={styles.uploadText}>{loading ? '上传中...' : placeholder}</span>
    </div>
  );

  // 渲染图片预览
  const renderImagePreview = () => (
    <div className={styles.imagePreview} style={{ width, height }}>
      <img src={value} alt="preview" />
      {!disabled && (
        <div className={styles.imageOverlay}>
          <span className={styles.deleteButton} onClick={handleDelete}>
            <DeleteOutlined />
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className={cx(styles.uploadContainer, className)} style={style}>
      <Upload
        accept={accept}
        showUploadList={false}
        beforeUpload={beforeUpload}
        customRequest={customUpload}
        disabled={disabled || loading}
      >
        {value ? renderImagePreview() : renderUploadButton()}
      </Upload>
    </div>
  );
};

export default ImageUpload;
